import {
    AfterViewChecked,
    Directive,
    Input,
    OnDestroy,
    afterNextRender,
    afterRender,
    ElementRef,
    AfterRenderPhase,
} from '@angular/core';
import { Router } from '@angular/router';

import {
    DateInputComponent,
    DatePickerComponent,
    DateTimePickerComponent,
    TimePickerComponent,
} from '@progress/kendo-angular-dateinputs';
import {
    ComboBoxComponent,
    DropDownListComponent,
} from '@progress/kendo-angular-dropdowns';
import {
    TextAreaDirective,
    TextBoxComponent,
} from '@progress/kendo-angular-inputs';
import { TileLayoutItemComponent } from '@progress/kendo-angular-layout';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { DateForPmis } from '@pmis3/utils';
import { Subscription } from 'rxjs';
import {
    ButtonComponent,
    ButtonFillMode,
    ButtonSize,
} from '@progress/kendo-angular-buttons';
import { DialogComponent } from "@progress/kendo-angular-dialog";
import type { ActionsLayout } from '@progress/kendo-angular-dialog';

@Directive({
    selector: 'kendo-dateinput',
})
export class KendoDateInputExtraDirective {
    constructor(private host: DateInputComponent) {
        host.formatPlaceholder = 'formatPattern';
    }
}

@Directive({
    selector: 'kendo-datepicker',
})
export class KendoDatePickerExtraDirective {
    constructor(private host: DatePickerComponent) {
        host.formatPlaceholder = 'formatPattern';
    }
}

@Directive({
    selector: 'kendo-timepicker',
})
export class KendoTimePickerExtraDirective {
    constructor(private host: TimePickerComponent) {
        host.format = 'HH:mm';
        host.formatPlaceholder = 'formatPattern';
        host.nowButton = new DateForPmis().getTime() === new Date().getTime();
    }
}

@Directive({
    selector: 'kendo-datetimepicker',
})
export class KendoDateTimePickerExtraDirective {
    constructor(private host: DateTimePickerComponent) {
        host.formatPlaceholder = 'formatPattern';
    }
}

@Directive({
    selector: 'kendo-combobox',
})
export class KendoComboBoxExtraDirective {
    constructor(private host: ComboBoxComponent) {
        host.onFocus.subscribe(() => host.toggle(true));
    }
}

@Directive({
    selector: 'kendo-textbox',
})
export class KendoTextBoxExtraDirective {
    constructor(private host: TextBoxComponent) {
        host.showSuccessIcon = false;
        host.showErrorIcon = false;
    }
}

// when using tilelayout, do not set title or kendo-tilelayout-item-header as it's always the grab icon
@Directive({
    selector: 'kendo-tilelayout-item',
})
export class KendoTilelayoutItemExtraDirective {
    constructor(private host: TileLayoutItemComponent) {
        host.title = 'T';
    }
}

@Directive({
    selector: '[kendoTextArea][autosizeFix]',
})
export class KendoTextAreaExtraDirective implements AfterViewChecked {
    constructor(private host: TextAreaDirective) {}

    ngAfterViewChecked(): void {
        this.host.autoSize = true;
        this.host.ngOnChanges({ autoSize: this.host.autoSize });
    }
}

@Directive({
    selector: '[kendoTooltip]',
    exportAs: 'kendoTooltip',
})
export class TooltipExtraDirective implements OnDestroy {
    private subscription: Subscription;

    constructor(private host: TooltipDirective, private routeService: Router) {
        const isMobile = false; //TODO;
        this.host.showOn = isMobile ? 'none' : 'hover';
        this.host.collision = { horizontal: 'fit', vertical: 'fit' };
        this.subscription = this.routeService.events.subscribe((r) =>
            host.hide()
        );
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }
}

@Directive({
    selector: 'kendo-dropdownlist[disabled]',
})
export class KendoDropDownListDisabledDirective {
    @Input() set disabled(isDisabled: boolean) {
        if (isDisabled) this.dropdownlist.toggle(false);
    }

    constructor(private dropdownlist: DropDownListComponent) {}
}
@Directive({
    selector: '[kendoButton]',
    exportAs: 'kendoButton',
})
export class KendoButtonExtraDirective {
    @Input() set fillMode(fillMode: ButtonFillMode) {
        if (!fillMode) this.host.fillMode = 'flat';

    }
    @Input() set size(size: ButtonSize) {
        if (!size) this.host.size = 'medium';
    }

    constructor(private host: ButtonComponent) {}
}
@Directive({
    selector: 'kendo-grid',
})
export class KendoGridExtraDirective {
    lastHeaderCellbgColor!: string;

    constructor(private el: ElementRef) {
        afterRender(
            () => {
                if (!this.lastHeaderCellbgColor) this.syncGridScroll();
                return;
            },
            { phase: AfterRenderPhase.Read }
        );
    }

    syncGridScroll() {
        const gridHeaderTable = this.el.nativeElement.querySelector(
            '.k-grid-header-wrap .k-grid-header-table'
        );
        const gridBody = this.el.nativeElement.querySelector('.k-grid-content');
        if (gridBody?.scrollHeight >= gridBody?.clientHeight) {
            const scrollbarWidth =
                gridBody?.offsetWidth - gridBody?.clientWidth;
            const lastHeaderCell = gridHeaderTable.querySelector(
                '.k-table-th.column-header:last-child'
            );
            if (lastHeaderCell)
                this.lastHeaderCellbgColor = window
                    .getComputedStyle(lastHeaderCell)
                    .getPropertyValue('background-color'); //get last element's background color
            gridHeaderTable.style.borderRight = `${
                scrollbarWidth + 1
            }px solid ${this.lastHeaderCellbgColor}`;
        }
    }
}

@Directive({
    selector: 'kendo-dialog',
})
export class KendoDialogExtraDirective {
    @Input() set actionsLayout(value: ActionsLayout) {
        this.host.actionsLayout = !value ? 'end' : value;
    }

    constructor(private host: DialogComponent) {
       
        afterNextRender(() => { 
            console.log('KendoDialogExtraDirective', host);
            if (host && host.dialog) {
                // Prevent dialog from closing when clicking inside the dialog
                // This is useful when using the dialog as a popover or dropdown
                host.dialog.nativeElement.addEventListener('click', (event: MouseEvent) => {
                    event.stopPropagation();
                });
            }
        }, {
            phase: AfterRenderPhase.MixedReadWrite,
        });
    }
}
